import { Check, Close, CopyAll, DataObject, Delete, FormatAlignCenter } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import { Alert, Box, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormHelperText, Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, TextareaAutosize, Typography, useTheme } from '@mui/material'
import { grey } from '@mui/material/colors'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Fragment, useEffect, useState } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { API_URL } from '../../app/const'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { selectAuthUser } from '../auth/AuthSlice'
import ValidDomainInputList from '../utils/validDomain/ValidDomainInputList'
import { ApiTokenObj, clearEmbedCode, deleteApiTokenAsync, fetchAllApiTokensAsync, getEmbedCodeAsync, hideToken, saveApiTokenAsync, selectAllApiTokens, selectApiToken, selectApiTokenVisible, selectEmbedCode } from './ApiTokenSlice'
import { GcloudTokenObj, deleteGcloudTokenAsync, fetchMyGcloudTokensAsync, genGcloudTokenAsync, selectGcloudTokenStatus, selectMyGcloudTokens } from './GcloudTokenSlice'

const ApiToken = () => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const dispatch = useAppDispatch()
  const apiTokens = useAppSelector(selectAllApiTokens)
  const showToken = useAppSelector(selectApiTokenVisible)
  const apiToken = useAppSelector(selectApiToken)
  const gcloudTokens = useAppSelector(selectMyGcloudTokens)
  const gCloudTokenStats = useAppSelector(selectGcloudTokenStatus)
  const authUser = useAppSelector(selectAuthUser)
  const [showNewTokenForm, setShowNewTokenForm] = useState(false)
  const [description, setDescription] = useState('')
  const [validDomains, setValidDomains] = useState('*')

  const [callbackEmail, setCallbackEmail] = useState('')
  const [callbackUrl, setCallbackUrl] = useState('')
  const [callbackSharedSecret, setCallbackSharedSecret] = useState('')

  const [copied, setCopied] = useState(false)
  const embedCode = useAppSelector(selectEmbedCode)
  const [showEmbedCodeDialog, setShowEmbedCodeDialog] = useState(false)
  const handleCloseCopied = () => { setCopied(false) }

  const paidTierCount = authUser?.org?.paidTierCount ?? authUser?.paidTierCount ?? 0

  useEffect(() => {
    dispatch(fetchAllApiTokensAsync({}))
    dispatch(fetchMyGcloudTokensAsync({}))
    return () => {
      dispatch(clearEmbedCode())
      dispatch(hideToken())
    }
  }, [dispatch])

  return <Box sx={{ textAlign: 'left' }}>
    <ButtonGroup sx={{ marginBottom: 5 }}>
      <Button disabled={showNewTokenForm} onClick={() => setShowNewTokenForm(true)}>New Token</Button>
    </ButtonGroup>
    <Typography variant='h6' sx={{ color: grey[700] }}>For documentation please go <a href="/api/docs">here</a> </Typography>

    {paidTierCount < 20 && (
      <Alert severity="warning" sx={{ marginBottom: 2, marginTop: 2 }}>
        API might stop working soon. Your current paid tier count is {paidTierCount}.
      </Alert>
    )}

    {showNewTokenForm && <Box sx={{ marginBottom: 10 }}>
      <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
        <TextField id="ip-text-config-key" label="Key description" variant="outlined"
          name="config-key" type="email" autoComplete="email"
          defaultValue={''} onChange={(e: any) => setDescription(e.target.value)} />
        <FormHelperText id="helper-text-config-key">Description of the API key</FormHelperText>
      </FormControl>
      <ValidDomainInputList value={validDomains} onChange={setValidDomains} />

      <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
        <TextField id="ip-text-callback-email" label="Callback Email" variant="outlined"
          name="callback-email" type="email" autoComplete="email"
          defaultValue={''} onChange={(e: any) => setCallbackEmail(e.target.value)} />
        <FormHelperText id="helper-text-callback-email">This email will be notified, once a report is generated using this API key</FormHelperText>
      </FormControl>

      <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
        <TextField id="ip-text-callback-url" label="Callback Url" variant="outlined"
          name="callback-url" type="email" autoComplete="email"
          defaultValue={''} onChange={(e: any) => setCallbackUrl(e.target.value)} />
        <FormHelperText id="helper-text-callback-url">This endpoint will be called, once a report is generated using this API key</FormHelperText>
      </FormControl>

      <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
        <TextField id="ip-text-callback-shared-secret" label="Callback Shared Secret" variant="outlined"
          name="callback-shared-secret" type="email" autoComplete="email"
          defaultValue={''} onChange={(e: any) => setCallbackSharedSecret(e.target.value)} />
        <FormHelperText id="helper-text-callback-shared-secret">Highly recommended</FormHelperText>
      </FormControl>

      <Button variant="outlined" endIcon={<Check />} sx={{ marginRight: 2 }} onClick={() => {
        dispatch(saveApiTokenAsync({
          apiToken: {
            apiAccessId: 0,
            maskedApiKey: '',
            version: '',
            description,
            validDomains,
            apiKey: '',
            expiry: '',
            ownerId: '',
            ownerEmail: '',
            callbackEmail,
            callbackUrl,
            callbackSharedSecret
          }
        }))
        dispatch(genGcloudTokenAsync({ allowedReferrers: validDomains.split(',') }))
        setShowNewTokenForm(false)
      }}>Generate {gCloudTokenStats === 'loading' ? '...' : '.'}</Button>
      <Button variant="outlined" endIcon={<Close />} onClick={() => setShowNewTokenForm(false)}>Cancel</Button>
    </Box>}

    {(showToken || copied) && apiToken?.apiKey && <Box sx={{ marginBottom: 5 }}>
      <Typography variant='h6' sx={{ color: grey[700] }}>
        <strong>Note: </strong>Copy this api token and keep it safe, it will NOT be shown again. Treat it like a password.
      </Typography>
      <Typography variant='caption' sx={{ color: grey[700], userSelect: 'all' }} component='div'>
        {apiToken?.apiKey}
      </Typography>
      {apiToken?.validDomains?.length > 1 && <Typography variant='caption' sx={{ color: grey[700] }} component='div'>
        <strong>Can be used in: </strong>{apiToken?.validDomains}
      </Typography>}
      <Dialog fullScreen={fullScreen} open={showEmbedCodeDialog && embedCode !== ''}
        onClose={() => setShowEmbedCodeDialog(false)} aria-labelledby="responsive-dialog-title">
        <DialogTitle>
          Use the following code to embed rentfinder.ai into your website.
          <Typography variant='caption' sx={{ color: grey[700] }} component='div'>
            <strong>Note: </strong>Copy this code and keep it safe, it will NOT be shown again. Treat it like a password. Limit its exposure by restricting the domains it can be used in. This will work only in <strong>{apiToken?.validDomains}</strong>
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <TextareaAutosize maxRows={10} minRows={3} style={{ width: '100%' }} value={embedCode} onFocus={(e: any) => {
              e.target.select()
            }} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Box sx={{ marginBottom: 5 }}>
            <ButtonGroup sx={{ marginBottom: 2 }}>
              <CopyToClipboard onCopy={() => setCopied(true)} text={embedCode ?? ''}>
                <Button variant='outlined' size='small' color='info' sx={{ marginTop: 2 }}>
                  <CopyAll />
                </Button>
              </CopyToClipboard>
              <Button variant='outlined' size='small' color='error' sx={{ marginTop: 2 }} onClick={() => { setShowEmbedCodeDialog(false) }}>
                <Close />
              </Button>
            </ButtonGroup>
          </Box>
        </DialogActions>
      </Dialog>
      <Button disabled={apiToken?.validDomains?.length <= 0} variant='outlined' size='small' color='success' sx={{ marginTop: 2 }} onClick={() => {
        dispatch(getEmbedCodeAsync({
          googleMapsApiKey: gcloudTokens.length > 0 ? gcloudTokens[0].keyString : 'REPLACEME#googleMapsApiKey',
          rentfinderApiBaseUrl: API_URL,
          rentfinderApiToken: apiToken?.apiKey,
          type: 'simple'
        }))
        setShowEmbedCodeDialog(true)
      }}><DataObject />Embed Simple</Button>
      <Button disabled={apiToken?.validDomains?.length <= 0} variant='outlined' size='small' color='success' sx={{ marginTop: 2 }} onClick={() => {
        dispatch(getEmbedCodeAsync({
          googleMapsApiKey: gcloudTokens.length > 0 ? gcloudTokens[0].keyString : 'REPLACEME#googleMapsApiKey',
          rentfinderApiBaseUrl: API_URL,
          rentfinderApiToken: apiToken?.apiKey,
          type: 'withPreContact'
        }))
        setShowEmbedCodeDialog(true)
      }}><FormatAlignCenter />Embed with Contacts (prefill)</Button>

      <Button disabled={apiToken?.validDomains?.length <= 0} variant='outlined' size='small' color='success' sx={{ marginTop: 2 }} onClick={() => {
        dispatch(getEmbedCodeAsync({
          googleMapsApiKey: gcloudTokens.length > 0 ? gcloudTokens[0].keyString : 'REPLACEME#googleMapsApiKey',
          rentfinderApiBaseUrl: API_URL,
          rentfinderApiToken: apiToken?.apiKey,
          type: 'withPostContact'
        }))
        setShowEmbedCodeDialog(true)
      }}><FormatAlignCenter />Embed with Contacts (ppostfill)</Button>
      <CopyToClipboard onCopy={() => setCopied(true)}
        text={apiToken?.apiKey}>
        <Button variant='outlined' size='small' color='info' sx={{ marginTop: 2 }}>
          <CopyAll /> Copy Key
        </Button>
      </CopyToClipboard>
      <Button variant='outlined' size='small' color='error' sx={{ marginTop: 2 }} onClick={() => {
        dispatch(hideToken())
      }}>
        <Close /> Close
      </Button>
    </Box>}
    <Snackbar open={copied} autoHideDuration={6000} onClose={handleCloseCopied}>
      <Alert icon={<CheckIcon fontSize="inherit" />} onClose={handleCloseCopied} sx={{ width: '100%' }}>
        Copied!
      </Alert>
    </Snackbar>

    <Typography variant='h6' sx={{ color: grey[700], marginTop: 5 }}>Rentfinder Api Keys</Typography>
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>
            <TableCell>Key</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {apiTokens?.map((apiToken: ApiTokenObj) => <Fragment key={apiToken.apiAccessId}>
            <TableRow>
              <TableCell scope="row" sx={{ borderBottom: 'none' }}>
                {apiToken.maskedApiKey ?? apiToken.apiKey}
              </TableCell>
              <TableCell sx={{ borderBottom: 'none' }}>{apiToken.description}</TableCell>
              <TableCell sx={{ borderBottom: 'none' }}>
                <ButtonGroup>
                  <Button variant='outlined' color='error' onClick={() => {
                    dispatch(deleteApiTokenAsync({ apiToken }))
                  }}>
                    <Delete />
                  </Button>
                </ButtonGroup>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={3}>
                <Typography variant='caption' sx={{ color: grey[700] }}>
                  <strong>Expiry: </strong>{apiToken.expiry}
                  <br />
                  <strong>Valid in: </strong>{apiToken.validDomains}
                  {apiToken.callbackEmail && <Fragment>
                    <br />
                    <strong>Valid in: </strong>{apiToken.callbackEmail}
                  </Fragment>}
                  {apiToken.callbackUrl && <Fragment>
                    <br />
                    <strong>Valid in: </strong>{apiToken.callbackUrl}
                  </Fragment>}
                  {apiToken.callbackSharedSecret && <Fragment>
                    <br />
                    <strong>Valid in: </strong>{apiToken.callbackSharedSecret}
                  </Fragment>}
                </Typography>
              </TableCell>
            </TableRow>
          </Fragment>)}
        </TableBody>
      </Table>
    </TableContainer>

    <Typography variant='h6' sx={{ color: grey[700], marginTop: 5 }}>Google maps Api Keys</Typography>
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} size="small" aria-label="Google maps api key table">
        <TableHead>
          <TableRow>
            <TableCell>Key</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {gcloudTokens?.map((gcloudToken: GcloudTokenObj, index: number) => <Fragment key={index}>
            <TableRow>
              <TableCell scope="row" sx={{ borderBottom: 'none' }}>
                {gcloudToken.keyString}
              </TableCell>
              <TableCell sx={{ borderBottom: 'none' }}>{gcloudToken.uid}</TableCell>
              <TableCell sx={{ borderBottom: 'none' }}>
                <ButtonGroup>
                  <Button variant='outlined' color='error' onClick={() => {
                    dispatch(deleteGcloudTokenAsync({ gCloudKeyName: gcloudToken.name }))
                  }}>
                    <Delete />
                  </Button>
                </ButtonGroup>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={3}>
                <Typography variant='caption' sx={{ color: grey[700] }}>
                  <strong>Valid in: </strong>{gcloudToken.restrictions?.browserKeyRestrictions?.allowedReferrers?.join(', ')}
                </Typography>
              </TableCell>
            </TableRow>
          </Fragment>)}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
}

export default ApiToken